import { Field, FieldProps } from 'formik';
import React, { ReactNode } from 'react';
import { InjectedIntl, injectIntl } from 'react-intl';
import styled from 'styled-components';
import { IMasterAddress } from '../../../../api/addressSearch/addressSearch';
import { AddressSearchService } from '../../../../api/addressSearch/addressSearchAPI';
import { TranslationHelpers } from '../../../../i18n/translationHelpers';
import ValidationErrors from '../validationErrors';
import InputField from '../../components/InputField';
import { COLORS, FONT_FAMILY, MQS } from '../../constants/styles';
import {
    TwoColFormLeftElementLink,
    TwoColFormRightElement,
} from '../../components/FormLayoutStyles';
import Icon from '../../components/Icon';
import ICONS from '../../constants/icons';
import { SupportedLanguage } from '../../../../config/constants/supportedLanguage.enum';
import { ErrorResolution, ErrorResolutionTypes } from '../../errorResolution/errorResolution';
import Checkbox from '../../components/Checkbox';
import { PostalCodeValidation } from './PostalCodeValidation';
import ValueFormatters from '../valueFormatters';

interface IPostalCodeFieldProps {
    autoFocus?: boolean;
    intl: InjectedIntl;
    value?: string;
    required?: boolean;
    locale: SupportedLanguage;
    setMasterAddress?: (masterAddress: IMasterAddress | null) => void;
    showAddressSearchError?: (error: ErrorResolution) => void;
    changeStateInputDisabled: (status: boolean) => void;
}

export const TwoColFormWrapper = styled.div`
    display: flex;
    flex-direction: column;
    @media (min-width: ${MQS.mq3Min}) {
        flex-direction: row;
        margin-top: 50px;
        margin-bottom: 40px;
    }
`;

const ExternalLink = styled.a`
    color: ${COLORS.linkBlue};
    cursor: pointer;
    transition: font-weight 200ms ease-in;
    font-size: 0.8125rem;
    text-decoration: none;
    ${FONT_FAMILY.multipleRegularCss};
    display: flex;
    align-items: center;
    margin-right: 8px;
    &:hover {
        font-weight: bold;
    }
    & + & {
        margin-top: 30px;
    }
`;

const NyIcon = styled.div`
    margin-left: 5px;
    display: flex;
    align-items: center;
`;
export const TwoColFormLeftElement = styled.div`
    flex: 1 1 100%;
    margin-top: 35px;
    margin-bottom: 35px;

    @media (min-width: ${MQS.mq3Min}) {
        flex: 1 1 48%;
        margin-right: 4%;
        margin-top: 0;
        margin-bottom: 0;
    }
`;

const ElementLink = styled(TwoColFormLeftElementLink)`
    & > .oversea-check {
        padding-right: 0;
    }

    & label {
        color: ${COLORS.linkBlue};
        font-size: 0.8125rem;
    }
`;

class JPPostalCodeField extends React.Component<IPostalCodeFieldProps> {
    private readonly ID: string;
    private readonly LABEL_ID: string;
    private readonly ERROR_REQUIRED_ID: string;
    private readonly MAX_LENGTH: number;

    constructor(props: IPostalCodeFieldProps) {
        super(props);

        this.ID = 'postalCode';
        this.LABEL_ID = 'label.postal_code';
        this.ERROR_REQUIRED_ID = 'error.postal_code.required';
        this.MAX_LENGTH = 8;

        this.validate = this.validate.bind(this);
        this.renderInputField = this.renderInputField.bind(this);
    }

    public render() {
        return <Field name={this.ID} validate={this.validate} render={this.renderInputField} />;
    }

    private validate(value: string): string {
        const { intl, required } = this.props;

        if (!value && required) {
            return intl.formatMessage(
                TranslationHelpers.getMessageDescriptor(this.ERROR_REQUIRED_ID),
            );
        }

        if (!PostalCodeValidation.validateJPPostalCode(value)) {
            return ValidationErrors.fieldInvalidMessage(intl, this.LABEL_ID);
        }

        return '';
    }

    identifyPostalCode = (value: string): boolean[] => {
        const isJPPostalCode = PostalCodeValidation.validateJPPostalCode(value ?? '');
        const isOverSeasPostalCode = PostalCodeValidation.isOverSeasPostalCode(value ?? '');
        return [isJPPostalCode, isOverSeasPostalCode];
    };

    componentDidMount() {
        const initialValue = this.props.value ?? '';
        const [isJPPostalCode, isOverSeasPostalCode] = this.identifyPostalCode(initialValue);
        if (isJPPostalCode && !isOverSeasPostalCode) {
            AddressSearchService.searchAddress(initialValue).then((res) => {
                const address = res.address;
                if (typeof this.props.setMasterAddress === 'function') {
                    this.props.setMasterAddress(address);
                }
            });
        }
    }

    private renderInputField({
        form: { touched, errors, setFieldValue, setFieldTouched, handleChange },
    }: FieldProps): ReactNode {
        const {
            autoFocus,
            value,
            required,
            setMasterAddress,
            showAddressSearchError,
            changeStateInputDisabled,
            intl,
        } = this.props;
        const isTouched = touched && touched[this.ID];
        const errorMessage = isTouched && errors && errors[this.ID] ? errors[this.ID] : undefined;
        const isNotOverSeasPostalCode = !PostalCodeValidation.isOverSeasPostalCode(
            this.props.value ?? '',
        );

        const autoFillPostalCodeClick = (isNotOversea: boolean) => {
            if (isNotOversea) {
                setFieldValue(this.ID, '000-0000');
                setFieldValue('state', '国外');
            } else {
                setFieldValue(this.ID, '');
                setFieldValue('state', '');
            }
            setFieldValue('city', '');
            setFieldValue('addressLine1', '');
            if (typeof setMasterAddress === 'function') {
                setMasterAddress(null);
            }
            changeStateInputDisabled(true);
        };

        const changeHandle = (event: React.ChangeEvent<HTMLInputElement>) => {
            event.target.value = PostalCodeValidation.formatPostalCode(event.target.value);
            const [isJPPostalCode, isOverSeasPostalCode] = this.identifyPostalCode(
                event.target.value,
            );
            handleChange(event);
            if (isJPPostalCode && !isOverSeasPostalCode) {
                AddressSearchService.searchAddress(event.target.value).then((res) => {
                    const address = res.address;
                    if (typeof setMasterAddress === 'function') {
                        setMasterAddress(address);
                    }
                    if (address !== null) {
                        setFieldValue('state', address.state);
                        setFieldValue('city', address.city);
                        setFieldValue(
                            'addressLine1',
                            ValueFormatters.trimExtraSpaces(address.street),
                        );
                        setFieldTouched('addressLine1', true);
                    } else {
                        if (res.error && typeof showAddressSearchError === 'function') {
                            showAddressSearchError(res.error);
                        }
                        setFieldValue('state', '');
                        setFieldValue('city', '');
                        setFieldValue('addressLine1', '');
                        changeStateInputDisabled(false);
                        return;
                    }
                });
            } else if (isOverSeasPostalCode) {
                if (typeof setMasterAddress === 'function') {
                    setMasterAddress(null);
                }
                setFieldValue('state', '国外');
                setFieldValue('city', '');
                setFieldValue('addressLine1', '');
                changeStateInputDisabled(true);
            }
        };
        const overseasLabel = intl.formatMessage(
            TranslationHelpers.getMessageDescriptor('address.set_overseas'),
        );
        return (
            <TwoColFormWrapper data-qa-id={this.ID}>
                {isNotOverSeasPostalCode && (
                    <TwoColFormLeftElement>
                        <InputField
                            autoComplete="postal-code"
                            labelMessage={TranslationHelpers.getMessageDescriptor(this.LABEL_ID)}
                            type="tel"
                            id={this.ID}
                            maxLength={this.MAX_LENGTH}
                            name={this.ID}
                            value={value}
                            error={errorMessage}
                            autoFocus={autoFocus}
                            required={required}
                            onChange={changeHandle}
                        />
                    </TwoColFormLeftElement>
                )}
                <TwoColFormRightElement>
                    <ElementLink>
                        {isNotOverSeasPostalCode && (
                            <ExternalLink
                                href={'https://www.post.japanpost.jp/zipcode/'}
                                target="_bank"
                            >
                                {TranslationHelpers.getFormattedMessage(
                                    'address.postalcode.search',
                                )}
                                <NyIcon>
                                    <Icon
                                        icon={ICONS.NEW_WINDOW}
                                        size={'13'}
                                        viewBoxMax={'26'}
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                    />
                                </NyIcon>
                            </ExternalLink>
                        )}
                        <Checkbox
                            id={'isOverSeasChecked'}
                            name={'isOverSeasChecked'}
                            ariaLabel={overseasLabel}
                            label={overseasLabel}
                            value={!isNotOverSeasPostalCode}
                            onChange={() => {
                                autoFillPostalCodeClick(isNotOverSeasPostalCode);
                            }}
                            tooltipText={''}
                            className="oversea-check"
                        />
                    </ElementLink>
                </TwoColFormRightElement>
            </TwoColFormWrapper>
        );
    }
}

export default injectIntl(JPPostalCodeField);
